import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react"
import { LexicalText, STARTING_NODES } from "@/schema"
import { $isLinkNode } from "@lexical/link"
import { $convertFromMarkdownString, HEADING } from "@lexical/markdown"
import { CheckListPlugin } from "@lexical/react/LexicalCheckListPlugin"
import { InitialConfigType, LexicalComposer } from "@lexical/react/LexicalComposer"
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext"
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary"
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin"
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin"
import { ListPlugin } from "@lexical/react/LexicalListPlugin"
import { MarkdownShortcutPlugin } from "@lexical/react/LexicalMarkdownShortcutPlugin"
import { PlainTextPlugin } from "@lexical/react/LexicalPlainTextPlugin"
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin"
import { TabIndentationPlugin } from "@lexical/react/LexicalTabIndentationPlugin"
import { TablePlugin } from "@lexical/react/LexicalTablePlugin"
import { $findMatchingParent, isHTMLAnchorElement } from "@lexical/utils"
import { cn } from "@ui/utils"
import {
  $createParagraphNode,
  $getNearestNodeFromDOMNode,
  $getRoot,
  LexicalEditor,
  SerializedEditorState,
} from "lexical"

import { LEXICAL_NODES, MARKDOWN_TRANSFORMERS } from "@/components/lexical/config"
import LexicalContentEditable from "@/components/lexical/core/content-editable"
import baseStyles from "@/components/lexical/editors/base.module.css"
import { PlaceholderData, registerPlaceholders } from "@/components/lexical/placeholders"
import BlockPlaceholderPlugin from "@/components/lexical/plugins/block-placeholder"
import ClearFormattingOnNewBlockPlugin from "@/components/lexical/plugins/clear-formatting-on-new-block"
import EnsureEditablePlugin from "@/components/lexical/plugins/ensure-editable"
import FloatingLinkEditorPlugin, { validateLinkUrl } from "@/components/lexical/plugins/floating-link-editor"
import FloatingLinkPreviewPlugin from "@/components/lexical/plugins/floating-link-preview"
import FloatingTextFormatToolbarPlugin from "@/components/lexical/plugins/floating-text-format-toolbar"
import InlineImagePlugin from "@/components/lexical/plugins/inline-image"
import ListMaxIndentLevelPlugin from "@/components/lexical/plugins/list-max-indent-level"
import OnContentChangePlugin from "@/components/lexical/plugins/on-content-change"
import OnEnterPlugin from "@/components/lexical/plugins/on-enter"
import PasteNormalizePlugin from "@/components/lexical/plugins/paste-normalize"
import PlaceholderPlugin from "@/components/lexical/plugins/placeholder"
import SlashCommandPlugin from "@/components/lexical/plugins/slash-command"
import TableHoverActionsPlugin from "@/components/lexical/plugins/table-hover-actions"
import { htmlToNodes, markdownToNodes, normalizeListIndent } from "@/components/lexical/serializer"

export interface EditorFocusOptions {
  defaultSelection?: "rootStart" | "rootEnd"
}

export interface EditorRefHandle {
  focus: (options?: EditorFocusOptions) => void
  blur: () => void
  updateContent: (content: LexicalText) => void
  getEditor: () => LexicalEditor | null
  getDescriptionEditor?: () => LexicalEditor | null
}

export interface EditorProps {
  onChange?: (lexicalText: LexicalText) => void
  onEnter?: () => void
  placeholder?: string
  startingPlaceholder?: string
  initialContent?: LexicalText
  isPlainText?: boolean
  proseClasses?: string
  styleOverrides?: React.CSSProperties | Record<`--${string}`, string>
  children?: React.ReactNode
  disableHeadings?: boolean
  disableSlashCommands?: boolean
  editable?: boolean
  markdown?: string
  onHtmlConverted?: (lexicalText: LexicalText) => void
  onMarkdownConverted?: (lexicalText: LexicalText) => void
  shouldIgnoreEnter?: () => boolean
  enablePlaceholders?: boolean
  placeholders?: PlaceholderData[]
}

/** Plugin that captures the LexicalEditor instance so we can call .focus() */
function CaptureEditorPlugin({ setEditor }: { setEditor: (editor: LexicalEditor) => void }) {
  const [lexicalEditor] = useLexicalComposerContext()
  React.useEffect(() => {
    setEditor(lexicalEditor)
  }, [lexicalEditor, setEditor])
  return null
}

function ModClickLinkPlugin() {
  const [editor] = useLexicalComposerContext()
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (!event.metaKey && !event.ctrlKey) return

      const target = event.target
      if (!(target instanceof Node)) return

      let url: string | null = null
      editor.update(() => {
        const clickedNode = $getNearestNodeFromDOMNode(target)
        if (clickedNode === null) return
        const maybeLinkNode = $findMatchingParent(clickedNode, (node) => $isLinkNode(node))
        if ($isLinkNode(maybeLinkNode)) {
          url = maybeLinkNode.sanitizeUrl(maybeLinkNode.getURL())
        } else if (target instanceof HTMLElement) {
          const anchor = target.closest("a")
          if (anchor && isHTMLAnchorElement(anchor)) {
            url = anchor.href
          }
        }
      })

      if (url) {
        window.open(url, "_blank")
        event.preventDefault()
      }
    }

    return editor.registerRootListener((rootElement, prevRootElement) => {
      prevRootElement?.removeEventListener("click", onClick)
      rootElement?.addEventListener("click", onClick)
    })
  }, [editor])
  return null
}

function EditorComponent(props: EditorProps, forwardedRef: React.Ref<EditorRefHandle>) {
  const {
    onChange,
    onEnter,
    startingPlaceholder = "Start typing or press '/' for commands",
    placeholder,
    initialContent,
    isPlainText = false,
    proseClasses = "prose-lexical dark:prose-invert",
    styleOverrides,
    children,
    disableHeadings = false,
    disableSlashCommands = false,
    editable = true,
    markdown,
    onHtmlConverted,
    onMarkdownConverted,
    shouldIgnoreEnter,
    enablePlaceholders = false,
    placeholders,
  } = props

  if (placeholders && placeholders.length) {
    registerPlaceholders(placeholders)
  }

  const [floatingAnchorElem, setFloatingAnchorElem] = useState<HTMLDivElement | null>(null)
  const [lexicalEditor, setLexicalEditor] = useState<LexicalEditor | null>(null)
  const [editorContent, setEditorContent] = useState<SerializedEditorState | undefined>(initialContent?.nodes)
  const hasConvertedRef = useRef(false)

  const emptyContent = () => {
    const paragraph = $createParagraphNode()
    $getRoot().append(paragraph)
  }

  useEffect(() => {
    if (!initialContent || (!initialContent.nodes && !initialContent.html && !initialContent.markdown)) {
      setEditorContent(STARTING_NODES)
      return
    }

    if (initialContent.nodes && "root" in initialContent.nodes) {
      setEditorContent(initialContent.nodes)
    } else if (initialContent.html && !hasConvertedRef.current) {
      hasConvertedRef.current = true

      const convertedNodes = htmlToNodes(initialContent.html)

      if (convertedNodes) {
        setEditorContent(convertedNodes)

        if (onHtmlConverted) {
          onHtmlConverted({
            html: initialContent.html,
            nodes: convertedNodes,
          })
        }
      }
    } else if (initialContent.markdown && !hasConvertedRef.current) {
      hasConvertedRef.current = true
      const convertedNodes = markdownToNodes(initialContent.markdown)

      if (convertedNodes) {
        setEditorContent(convertedNodes)

        if (onMarkdownConverted) {
          onMarkdownConverted({
            markdown: initialContent.markdown,
            nodes: convertedNodes,
          })
        }
      }
    }
  }, [initialContent, onHtmlConverted, onMarkdownConverted])

  useImperativeHandle(forwardedRef, () => ({
    focus: (options?: EditorFocusOptions) => {
      if (lexicalEditor) {
        lexicalEditor.focus(undefined, options)
      }
    },
    blur: () => {
      if (lexicalEditor) {
        lexicalEditor.blur()
      }
    },
    updateContent: (content: LexicalText) => {
      if (!lexicalEditor) return

      if (content.nodes) {
        lexicalEditor.update(() => {
          const editorState = lexicalEditor.parseEditorState(content.nodes || "")
          lexicalEditor.setEditorState(editorState)
        })
      } else if (content.markdown) {
        const convertedNodes = markdownToNodes(content.markdown)
        if (convertedNodes) {
          lexicalEditor.update(() => {
            const editorState = lexicalEditor.parseEditorState(convertedNodes)
            lexicalEditor.setEditorState(editorState)
          })
        }
      } else if (content.html) {
        const convertedNodes = htmlToNodes(content.html)
        if (convertedNodes) {
          lexicalEditor.update(() => {
            const editorState = lexicalEditor.parseEditorState(convertedNodes)
            lexicalEditor.setEditorState(editorState)
          })
        }
      }
    },
    getEditor: () => lexicalEditor,
  }))

  if (!editorContent) {
    // Only return empty if we have HTML that hasn't been converted yet
    if (initialContent && (initialContent.html || initialContent.markdown) && !hasConvertedRef.current) {
      return <></>
    }

    // Otherwise, proceed with rendering (will use emptyContent fallback)
  }

  const initialConfig: InitialConfigType = {
    namespace: "SlantEditor",
    onError: (error) => console.error(error),
    theme: {
      text: {
        underline: baseStyles.underline,
        strikethrough: baseStyles.strikethrough,
        underlineStrikethrough: baseStyles.underlineStrikethrough,
      },
      link: baseStyles.link,
      table: baseStyles.table,
      tableScrollableWrapper: baseStyles.tableScrollableWrapper,
      tableCell: baseStyles.tableCell,
      tableCellHeader: baseStyles.tableCellHeader,
      tableCellSelected: baseStyles.tableCellSelected,
      tableSelected: baseStyles.tableSelected,
      tableCellResizer: baseStyles.tableCellResizer,
      tableSelection: baseStyles.tableSelection,
      inlineImage: baseStyles.inlineImage,
      list: {
        nested: {
          listitem: baseStyles.nestedListItem,
        },
        checklist: baseStyles.checklist,
        listitemChecked: baseStyles.listItemChecked,
        listitemUnchecked: baseStyles.listItemUnchecked,
      },
    },
    nodes: [...LEXICAL_NODES],
    editorState: markdown
      ? () => $convertFromMarkdownString(normalizeListIndent(markdown), MARKDOWN_TRANSFORMERS)
      : editorContent && "root" in editorContent
        ? JSON.stringify(editorContent)
        : emptyContent,
    editable,
  }

  const onRef = (_floatingAnchorElem: HTMLDivElement) => {
    if (_floatingAnchorElem) {
      setFloatingAnchorElem(_floatingAnchorElem)
    }
  }

  const contentEditableElem = (
    <div className={baseStyles.editorContainer} ref={onRef}>
      <LexicalContentEditable placeholder={startingPlaceholder} proseClasses={proseClasses} />
    </div>
  )

  const transformers = disableHeadings ? MARKDOWN_TRANSFORMERS.filter((t) => t !== HEADING) : MARKDOWN_TRANSFORMERS

  const getPreferredFormat = (): "html" | "markdown" => {
    if (initialContent?.preferred_format) {
      return initialContent.preferred_format
    }

    return "html"
  }

  const preferredFormat = getPreferredFormat()

  return (
    <div className={cn(baseStyles.base, "h-auto")} style={styleOverrides as React.CSSProperties}>
      <LexicalComposer initialConfig={initialConfig}>
        <CaptureEditorPlugin setEditor={setLexicalEditor} />
        <HistoryPlugin />
        <EnsureEditablePlugin />
        {!isPlainText && <PasteNormalizePlugin />}

        {children}

        {enablePlaceholders && <PlaceholderPlugin placeholders={placeholders} />}

        {isPlainText ? (
          <PlainTextPlugin ErrorBoundary={LexicalErrorBoundary} contentEditable={contentEditableElem} />
        ) : (
          <RichTextPlugin ErrorBoundary={LexicalErrorBoundary} contentEditable={contentEditableElem} />
        )}

        {onChange && <OnContentChangePlugin onSave={onChange} preferredFormat={preferredFormat} />}

        {!isPlainText && (
          <>
            <ListPlugin />
            <CheckListPlugin />
            <ListMaxIndentLevelPlugin />
            <TablePlugin hasTabHandler={false} hasHorizontalScroll={true} />
            <LinkPlugin validateUrl={validateLinkUrl} />
            <ModClickLinkPlugin />
            <MarkdownShortcutPlugin transformers={transformers} />
            <ClearFormattingOnNewBlockPlugin />

            <TabIndentationPlugin />

            {editable && (
              <>
                <BlockPlaceholderPlugin placeholder={placeholder} />
                {!disableSlashCommands && (
                  <SlashCommandPlugin disableHeadings={disableHeadings} enableTable enableChecklist />
                )}
                <InlineImagePlugin />

                {floatingAnchorElem && (
                  <>
                    <FloatingTextFormatToolbarPlugin anchorElem={floatingAnchorElem} />
                    <FloatingLinkEditorPlugin anchorElem={floatingAnchorElem} />
                    <FloatingLinkPreviewPlugin anchorElem={floatingAnchorElem} />
                    <TableHoverActionsPlugin />
                  </>
                )}
              </>
            )}
          </>
        )}

        {onEnter && <OnEnterPlugin onEnter={onEnter} shouldIgnore={shouldIgnoreEnter} />}
      </LexicalComposer>
    </div>
  )
}

export const Editor = forwardRef<EditorRefHandle, EditorProps>(EditorComponent)
